// src/pages/Register.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import PlanSelectionModal from '../components/modals/PlanSelectionModal';
import MnemonicModal from '../components/modals/MnemonicModal';
import EmailVerificationModal from '../components/modals/EmailVerificationModal';
import { encryptPhrase } from '../utils/cryptoUtils';
import { fetchSubscriptionPlans, registerUser, validateActivationCode } from '../utils/api';
import { isValidEmail, validatePassword } from '../utils/validators';
import { logEvent, pageview } from '../analytics';

import '../styles/global.css';
import '../styles/components/register.css';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    repeatPassword: '',
    planId: null,
    activationCode: '',
    userType: 'personal'
  });

  const [activationCodeStatus, setActivationCodeStatus] = useState({ state: 'idle', message: '', planName: '' });
  const [isActivationCodeApplied, setIsActivationCodeApplied] = useState(false);
  const [isValidatingActivationCode, setIsValidatingActivationCode] = useState(false);
  const [previousPlanBeforeActivation, setPreviousPlanBeforeActivation] = useState({ id: null, name: '' });

  const isFormValid = () => {
    return formData.firstName &&
      formData.lastName &&
      formData.email &&
      formData.password &&
      formData.repeatPassword &&
      formData.planId;
  };

  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [plans, setPlans] = useState([]);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showMnemonicModal, setShowMnemonicModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [mnemonic, setMnemonic] = useState('');

  const [alert, setAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    footer: '',
    type: 'success'
  });

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const plansData = await fetchSubscriptionPlans();
        setPlans(plansData);
      } catch (error) {
        showAlert(
          'Error',
          'Unable to load subscription plans.',
          'fail',
          'Please try again later.'
        );
      }
    };

    loadPlans();
  }, []);

   useEffect(() => {
   // Pageview for Register page (SPA)
   pageview('/register');
   // Event for marketing: form viewed
   logEvent('registration_form_viewed', {
     plan_selected: !!formData.planId,
     user_type: formData.userType || 'personal'
   });
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id === 'activationCode') {
      setActivationCodeStatus({ state: 'idle', message: '', planName: '' });
    }
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const showAlert = (title, message, type = 'success', footer = '') => {
    setAlert({
      isOpen: true,
      title,
      message,
      footer,
      type
    });

    if (type !== 'success') {
      setTimeout(() => {
        closeAlert();
      }, 5000);
    }
  };

  const closeAlert = () => {
    setAlert({ ...alert, isOpen: false });
  };

  const handlePlanSelect = (planId, planName) => {
    if (isActivationCodeApplied) {
      showAlert(
        'Activation Code Applied',
        'Remove the activation code to change your plan selection.',
        'warning'
      );
      return;
    }

    setFormData(prev => ({ ...prev, planId }));
    setSelectedPlanName(planName);
    setShowPlanModal(false);

       logEvent('registration_plan_selected', {
     plan_id: planId,
     plan_name: planName
   });

    const selectedPlan = plans.find(plan => plan.id === planId);
  if (selectedPlan && (selectedPlan.type === 'trial' || planName.toLowerCase().includes('trial'))) {
    showAlert(
      'Trial Plan Selected',
      'Important: All data will be permanently deleted after the trial period (14 days) if you do not upgrade to a paid plan.',
      'warning',
      'Make sure to upgrade before your trial expires to keep your data.'
    );
  }
  };

  const handleApplyActivationCode = async () => {
    if (!formData.activationCode || !formData.activationCode.trim()) {
      setActivationCodeStatus({
        state: 'error',
        message: 'Please enter an activation code before applying.',
        planName: ''
      });
      return;
    }

    setIsValidatingActivationCode(true);
    try {
      const result = await validateActivationCode(formData.activationCode.trim());
      setPreviousPlanBeforeActivation({ id: formData.planId, name: selectedPlanName });
      setFormData(prev => ({ ...prev, planId: result.planId }));
      setSelectedPlanName(result.planName || `Plan ID ${result.planId}`);
      setActivationCodeStatus({
        state: 'success',
        message: result.planName
          ? `Activation code applied for the ${result.planName} plan.`
          : 'Activation code applied.',
        planName: result.planName || ''
      });
      setIsActivationCodeApplied(true);
    } catch (error) {
      setActivationCodeStatus({
        state: 'error',
        message: error.message || 'Invalid activation code.',
        planName: ''
      });
    } finally {
      setIsValidatingActivationCode(false);
    }
  };

  const handleRemoveActivationCode = () => {
    setFormData(prev => ({
      ...prev,
      activationCode: '',
      planId: previousPlanBeforeActivation.id
    }));
    setSelectedPlanName(previousPlanBeforeActivation.name || '');
    setIsActivationCodeApplied(false);
    setActivationCodeStatus({ state: 'idle', message: '', planName: '' });
    setPreviousPlanBeforeActivation({ id: null, name: '' });
  };

  const validateFormData = () => {
    if (!formData.firstName || !formData.lastName || !formData.email ||
      !formData.password || !formData.repeatPassword || !formData.planId) {
      showAlert(
        'Error!',
        'All fields are required. Remember to select a plan.',
        'fail',
        ''
      );
      return false;
    }

    if (!isValidEmail(formData.email)) {
      showAlert(
        'Error!',
        'Invalid email used.',
        'fail',
        'Please correct the email and try again'
      );
      return false;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      showAlert(
        'Invalid Password',
        passwordValidation.message,
        'fail',
        'Please choose a stronger password.'
      );
      return false;
    }

    if (formData.password !== formData.repeatPassword) {
      showAlert(
        'Password Mismatch',
        'New password and confirm password do not match.',
        'fail',
        'Please ensure both password fields are identical.'
      );
      return false;
    }

    return true;
  };

  const handleRegistration = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      return;
    }

    setIsRegistering(true);

    try {
           // Track the submit attempt
     logEvent('registration_submitted', {
       plan_id: formData.planId,
       plan_name: selectedPlanName || '',
       user_type: formData.userType || 'personal',
       method: 'email'
     });

      const encryptedPassword = await encryptPhrase(formData.password);
      const result = await registerUser(formData, encryptedPassword);

      // Handle successful registration
      if (result.mnemonic) {
        setMnemonic(result.mnemonic);
        setShowMnemonicModal(true);
      } else {
        setShowEmailVerificationModal(true);
      }

    // Success events
     logEvent('sign_up', { method: 'email' });
     logEvent('registration_submitted_result', { result: 'success' });
    } catch (error) {
      showAlert(
        'Registration Failed',
        error.message || 'Unable to register.',
        'fail',
        'Please try again or contact support if the issue persists.'
      );
           // Failure event
     logEvent('registration_submitted_result', {
       result: 'failure',
       reason: error?.message || 'unknown'
     });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleMnemonicModalClose = () => {
    if (window.confirm('Make sure you have saved your Secure Phrases. Are you sure you want to close?')) {
      setShowMnemonicModal(false);
      setShowEmailVerificationModal(true);
    }
  };

  const handleContinue = () => {
    setShowMnemonicModal(false);
    setShowEmailVerificationModal(true);
  };

  const handleEmailVerificationModalClose = () => {
    setShowEmailVerificationModal(false);
    showAlert(
      'Registration Successful',
      'A Verification email has been sent!',
      'success',
      'Have a great day!'
    );
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  };

  return (
    <>
      <Navbar />

      <div className="container">
        <div className="register-card">
          <div className="card-body">
            <div className="text-center">
              <h1>Create an Account!</h1>
            </div>
            <form onSubmit={handleRegistration}>
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  id="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  id="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  className="form-control"
                  id="repeatPassword"
                  placeholder="Repeat Password"
                  value={formData.repeatPassword}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group activation-code-group">
                <label htmlFor="activationCode">Activation Code (optional)</label>
                <div className="activation-code-input-row">
                  <input
                    type="text"
                    className="form-control"
                    id="activationCode"
                    placeholder="Enter activation code"
                    value={formData.activationCode}
                    onChange={handleInputChange}
                    disabled={isActivationCodeApplied || isValidatingActivationCode}
                  />
                  {isActivationCodeApplied ? (
                    <button
                      type="button"
                      className="btn activation-code-action-btn remove-btn"
                      onClick={handleRemoveActivationCode}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn activation-code-action-btn"
                      onClick={handleApplyActivationCode}
                      disabled={isValidatingActivationCode || !(formData.activationCode && formData.activationCode.trim())}
                    >
                      {isValidatingActivationCode ? 'Applying...' : 'Apply'}
                    </button>
                  )}
                </div>
                {activationCodeStatus.state === 'success' && (
                  <p className="activation-code-success">
                    <span className="activation-code-check" aria-hidden="true">&#10003;</span>
                    {activationCodeStatus.planName
                      ? `Activation code applied for the ${activationCodeStatus.planName} plan.`
                      : 'Activation code applied.'}
                  </p>
                )}
                {activationCodeStatus.state === 'error' && (
                  <p className="activation-code-error">{activationCodeStatus.message}</p>
                )}
              </div>
              <button
                type="button"
                id="selectPlanButton"
                className={`btn select-plan-btn ${!formData.planId ? 'plan-not-selected' : 'plan-selected'}`}
                onClick={() => {
                  if (isActivationCodeApplied) {
                    showAlert(
                      'Activation Code Applied',
                      'Remove the activation code to change your plan selection.',
                      'warning'
                    );
                    return;
                  }
                  setShowPlanModal(true);
                }}
              >
                {isActivationCodeApplied
                  ? `Plan Locked: ${selectedPlanName || (formData.planId ? `Plan ID ${formData.planId}` : '')}`
                  : formData.planId ? `Selected Plan: ${selectedPlanName}` : 'Select a Plan'}
              </button>
              {isActivationCodeApplied && (
                <p className="activation-code-plan-note">
                  Plan selection is locked by your activation code. Remove the code to choose a different plan.
                </p>
              )}
              <button
                type="submit"
                className={`btn register-btn ${!isFormValid() ? 'disabled' : ''}`}
                disabled={isRegistering || !isFormValid()}
              >
                {isRegistering ? 'Registering...' : 'Register Account'}
              </button>
            </form>
            <hr />
            <div className="text-center">
              <a href="/forgot-password" className="small">Forgot Password?</a>
            </div>
            <div className="text-center">
              <span className="small">Already have an account?</span>
              <Link to="/login" className="card-link"> Login!</Link>
            </div>
          </div>
        </div>
      </div>

      <PlanSelectionModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        plans={plans}
        onSelectPlan={handlePlanSelect}
        selectedPlanId={formData.planId}
      />

      <MnemonicModal
        isOpen={showMnemonicModal}
        mnemonic={mnemonic}
        onClose={handleMnemonicModalClose}
        onContinue={handleContinue}
      />

      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={handleEmailVerificationModalClose}
      />

      <Alert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        footer={alert.footer}
        type={alert.type}
      />
    </>
  );
};

export default Register;